import { useCallback, useState } from "react";

import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import scrollToTheTop from "@lib/scrollToTheTop";

import { ORDER_MESSAGE_ZONE } from "../../../constants";
import useOrderFormik from "../../../hooks/useOrderFormik";
import updateAddressBookEntry from "../services/updateAddress";
import mapOrderDataToUpdateAddressData from "../utils/mapOrderDataToUpdateAddressData";

type TUpdateAddressReturn = {
  isLoading: boolean;
  updateAddress: () => Promise<boolean>;
};

const useUpdateAddress = (): TUpdateAddressReturn => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const { values, setErrors } = useOrderFormik();

  const setMessage = useMessage();

  const showAddressSaveErrorMessage = useCallback(() => {
    setMessage({
      message: t("order-message-failed_to_update_recipient"),
      props: {
        variant: "error",
        icon: <Icon icon="warning" size="1.45rem" />,
        canClose: true,
        padding: "0.45rem 0.75rem",
      },
      zone: ORDER_MESSAGE_ZONE,
    });
    scrollToTheTop();
  }, [setMessage, t]);

  const updateAddress = useCallback(async () => {
    setIsLoading(true);

    const addressData = mapOrderDataToUpdateAddressData(values);

    const result = await updateAddressBookEntry(addressData);

    setIsLoading(false);

    if (!result.isSuccess) {
      showAddressSaveErrorMessage();
      if (result.errorDetails) {
        setErrors(result.errorDetails);
      }
      return false;
    }

    return true;
  }, [setErrors, showAddressSaveErrorMessage, values]);

  return { isLoading, updateAddress };
};
export default useUpdateAddress;
